import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Wrench, Broom, Desktop, Confetti, ShieldCheck, Star, Briefcase } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import { submitServiceProApply } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";

const SERVICES = [
  { v: "handyman", label: "Handyman", icon: Wrench, color: "#14B8A6", desc: "Plumbing, electrical, AC, carpentry, repairs" },
  { v: "cleaning", label: "Cleaning", icon: Broom, color: "#D4AF37", desc: "Residential, commercial, deep & post-event" },
  { v: "tech_support", label: "Tech Support", icon: Desktop, color: "#5EEAD4", desc: "PC, networking, smart home, on-site IT" },
  { v: "events", label: "Events", icon: Confetti, color: "#FB7185", desc: "Bartending, decor, security, event crew" },
];

const PERKS = [
  { icon: Briefcase, title: "Set your own rates", desc: "You set what you charge — IslandHop never tells you what to bid." },
  { icon: ShieldCheck, title: "Vetted profile = more bookings", desc: "Verified pros get top placement in the app and more repeat customers." },
  { icon: Star, title: "Build a 5★ reputation", desc: "Reviews, repeat clients and a public portfolio that grows your brand." },
];

const INIT = {
  full_name: "",
  email: "",
  phone: "",
  location: "",
  service: "handyman",
  specialty: "",
  years_experience: 0,
  has_insurance: false,
  portfolio_url: "",
  notes: "",
};

export default function Pros() {
  useSeo({
    title: "Become a Service Pro",
    description: "Handyman, cleaning, tech support and events professionals — join the IslandHop network across Trinidad & Tobago.",
  });

  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitServiceProApply({
        ...form,
        years_experience: Number(form.years_experience) || 0,
        portfolio_url: form.portfolio_url || undefined,
      });
      toast.success(res.message);
      setSubmitted(true);
      setForm(INIT);
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="pros-page">
      <section className="relative pt-20 lg:pt-28 pb-16 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-40 [mask-image:linear-gradient(180deg,#000,transparent)]" />
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] ih-radial-teal" />

        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7 space-y-6">
            <SectionEyebrow label="Service Pros" color="electric" />
            <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
              Your skills.<br />
              <span className="text-white/40">Our customers.</span> <span className="text-ih-teal">More bookings.</span>
            </h1>
            <p className="text-white/70 text-lg max-w-xl leading-relaxed">
              IslandHop connects vetted local pros — handyman, cleaning, tech support and event crew — to
              thousands of customers across Trinidad &amp; Tobago. Set your own rates. Get paid weekly. Build
              your reputation.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a href="#apply" data-testid="pros-hero-cta" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-teal text-black font-semibold hover:scale-[1.02] transition ih-shadow-teal">
                Apply now
              </a>
              <a href="/drivers" className="text-sm text-white/60 hover:text-white transition underline underline-offset-4 decoration-ih-line2">
                I&apos;m a delivery driver →
              </a>
            </div>
          </motion.div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.v} className="bg-ih-surface border border-ih-line rounded-2xl p-5">
                  <Icon weight="duotone" size={24} className="mb-3" style={{ color: s.color }} />
                  <p className="font-display font-bold text-white">{s.label}</p>
                  <p className="text-xs text-white/55 mt-1 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid md:grid-cols-3 gap-4">
          {PERKS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="bg-ih-surface border border-ih-line rounded-2xl p-7 hover:border-ih-teal/40 transition">
                <Icon weight="duotone" size={28} className="text-ih-teal mb-4" />
                <p className="font-display font-bold text-xl text-white mb-2">{p.title}</p>
                <p className="text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Form */}
      <section id="apply" className="py-20 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24 self-start">
            <SectionEyebrow label="Service pro application" color="electric" />
            <h2 className="font-display font-extrabold text-4xl text-white leading-[1.05]">
              Tell us about<br />
              <span className="text-ih-teal">your trade.</span>
            </h2>
            <p className="text-white/60 leading-relaxed">
              We onboard pros in batches. Submit your details and our team will be in touch within 2 business
              days for verification.
            </p>
            <ul className="space-y-2 pt-2 text-sm text-white/65">
              <li>✓ Free to apply</li>
              <li>✓ ID + skill verification</li>
              <li>✓ Optional insurance check (more bookings if covered)</li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <SuccessCard onReset={() => setSubmitted(false)} />
            ) : (
              <form onSubmit={onSubmit} data-testid="pros-apply-form" className="bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-10 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Full name *">
                    <input data-testid="pros-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Email *">
                    <input type="email" data-testid="pros-email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="Phone *">
                    <input data-testid="pros-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+1 868 ..." />
                  </Field>
                  <Field label="Location *">
                    <input data-testid="pros-location" required value={form.location} onChange={(e) => update("location", e.target.value)} className={inputCls} placeholder="Port of Spain" />
                  </Field>
                  <Field label="Service *">
                    <select data-testid="pros-service" value={form.service} onChange={(e) => update("service", e.target.value)} className={inputCls}>
                      {SERVICES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Years of experience">
                    <input type="number" min="0" max="60" data-testid="pros-experience" value={form.years_experience} onChange={(e) => update("years_experience", e.target.value)} className={inputCls} />
                  </Field>
                </div>

                <Field label="Specialty / what you do best *">
                  <input data-testid="pros-specialty" required value={form.specialty} onChange={(e) => update("specialty", e.target.value)} className={inputCls} placeholder="e.g. Plumbing, AC repair, deep cleans, smart-home installs" />
                </Field>

                <Field label="Portfolio / website / Instagram (optional)">
                  <input type="url" data-testid="pros-portfolio" value={form.portfolio_url} onChange={(e) => update("portfolio_url", e.target.value)} className={inputCls} placeholder="https://" />
                </Field>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="pros-insurance"
                    checked={form.has_insurance}
                    onChange={(e) => update("has_insurance", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-ih-teal"
                  />
                  <span className="text-sm text-white/75">I carry valid liability/insurance coverage for my work</span>
                </label>

                <Field label="Anything else we should know?">
                  <textarea data-testid="pros-notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} className={`${inputCls} resize-none`} />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="pros-submit"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ih-teal text-black font-semibold hover:scale-[1.02] disabled:opacity-60 transition w-full sm:w-auto justify-center"
                >
                  {loading ? "Submitting..." : "Submit application"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-ih-line2 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-ih-teal transition text-sm";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-[0.16em] text-white/50">{label}</span>
    {children}
  </label>
);

const SuccessCard = ({ onReset }) => (
  <div data-testid="pros-success" className="bg-ih-surface border border-ih-teal/40 rounded-3xl p-10 lg:p-14 text-center ih-shadow-teal">
    <CheckCircle weight="duotone" size={56} className="mx-auto text-ih-teal mb-5" />
    <h3 className="font-display font-extrabold text-3xl text-white mb-3">Application received</h3>
    <p className="text-white/65 max-w-md mx-auto leading-relaxed">
      Welcome aboard. Our onboarding team will reach out within 2 business days for verification and next steps.
    </p>
    <button onClick={onReset} className="mt-6 text-sm text-ih-teal underline underline-offset-4">Submit another</button>
  </div>
);
